import { useState } from "react";
import { LMSLayout } from "@jambushrushti/components/lms/LMSLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import { Button } from "@jambushrushti/components/ui/button";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  ScrollText,
  Save,
  FileText,
  RotateCcw,
  Download,
  Printer,
  Search,
  Filter,
  Edit,
  Eye,
  Lock,
  AlertTriangle,
} from "lucide-react";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@jambushrushti/components/ui/tooltip";
import { useToast } from "@jambushrushti/hooks/use-toast";

const sampleManuscripts: {
  id: number;
  hastrapNo: string;
  granthName: string;
  mulkarta: string;
  tikakarta: string;
  language: string;
  category: string;
  folioNo: number;
  year: string;
  totalPages: number;
  availablePages: number;
  location: string;
  restricted: boolean;
  status: string;
}[] = [];

const categories = [
  "Agam",
  "Philosophy",
  "Grammar",
  "Logic",
  "Poetry",
  "Astronomy",
  "Medicine",
  "Ritual",
];

const languages = [
  "Prakrit",
  "Sanskrit",
  "Gujarati",
  "Hindi",
  "Marathi",
  "Apabhramsha",
];

const initialFormState = {
  hastrapNo: "",
  granthName: "",
  mulkarta: "",
  tikakarta: "",
  language: "",
  category: "",
  folioNo: "",
  year: "",
  totalPages: "",
  availablePages: "",
  location: "",
  remarks: "",
};

export default function Manuscripts() {
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [filters, setFilters] = useState({
    granthName: "",
    mulkarta: "",
    tikakarta: "",
    year: "",
    category: "",
    language: "",
    folioNo: "",
    location: "",
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitForApproval = async () => {
    if (!formData.hastrapNo || !formData.granthName) {
      toast({
        title: "Validation Error",
        description:
          "Please fill in required fields (Hastrap No and Granth Name)",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);

    toast({
      title: "Submitted for Approval",
      description: "Manuscript entry has been submitted for admin approval.",
    });
    setFormData(initialFormState);
  };

  const handleSaveAsDraft = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    setIsSubmitting(false);

    toast({
      title: "Draft Saved",
      description: "Manuscript entry has been saved as draft.",
    });
  };

  const handleResetForm = () => {
    setFormData(initialFormState);
    toast({
      title: "Form Reset",
      description: "All fields have been cleared.",
    });
  };

  return (
    <LMSLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Manuscripts</h1>
            <p className="text-muted-foreground">
              Manage precious manuscript collections
            </p>
          </div>
        </div>

        <Tabs defaultValue="entry" className="space-y-6">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="entry">New Entry</TabsTrigger>
            <TabsTrigger value="inquiry">Inquiry</TabsTrigger>
          </TabsList>

          <TabsContent value="entry" className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
              {/* Main Form */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ScrollText className="h-5 w-5 text-primary" />
                    Manuscript Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="hastrapNo">Hastrap No *</Label>
                      <Input
                        id="hastrapNo"
                        placeholder="e.g., H-001"
                        value={formData.hastrapNo}
                        onChange={e =>
                          handleInputChange("hastrapNo", e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="granthName">
                        Granth Name * (supports transliteration)
                      </Label>
                      <Input
                        id="granthName"
                        placeholder="Enter in Gujarati/Hindi/English"
                        value={formData.granthName}
                        onChange={e =>
                          handleInputChange("granthName", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="mulkarta">Mulkarta (Author)</Label>
                      <Input
                        id="mulkarta"
                        placeholder="Original author"
                        value={formData.mulkarta}
                        onChange={e =>
                          handleInputChange("mulkarta", e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tikakarta">Tikakarta (Commentator)</Label>
                      <Input
                        id="tikakarta"
                        placeholder="Commentator name"
                        value={formData.tikakarta}
                        onChange={e =>
                          handleInputChange("tikakarta", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="language">Language</Label>
                      <Select
                        value={formData.language}
                        onValueChange={value =>
                          handleInputChange("language", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                        <SelectContent>
                          {languages.map(lang => (
                            <SelectItem key={lang} value={lang.toLowerCase()}>
                              {lang}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={formData.category}
                        onValueChange={value =>
                          handleInputChange("category", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat.toLowerCase()}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="folioNo">Folio No</Label>
                      <Input
                        id="folioNo"
                        type="number"
                        placeholder="e.g., 127"
                        value={formData.folioNo}
                        onChange={e =>
                          handleInputChange("folioNo", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="space-y-2">
                      <Label htmlFor="year">Year / Century</Label>
                      <Input
                        id="year"
                        placeholder="e.g., 12th Century"
                        value={formData.year}
                        onChange={e =>
                          handleInputChange("year", e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="totalPages">Total Pages</Label>
                      <Input
                        id="totalPages"
                        type="number"
                        value={formData.totalPages}
                        onChange={e =>
                          handleInputChange("totalPages", e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="availablePages">Available Pages</Label>
                      <Input
                        id="availablePages"
                        type="number"
                        value={formData.availablePages}
                        onChange={e =>
                          handleInputChange("availablePages", e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        placeholder="Safe / Rack"
                        value={formData.location}
                        onChange={e =>
                          handleInputChange("location", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="remarks">Remarks</Label>
                    <Textarea
                      id="remarks"
                      placeholder="Additional notes about the manuscript..."
                      rows={3}
                      value={formData.remarks}
                      onChange={e =>
                        handleInputChange("remarks", e.target.value)
                      }
                    />
                  </div>

                  {/* Restricted Notice */}
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-500" />
                      <div>
                        <p className="font-medium text-amber-700 dark:text-amber-400">
                          Approval Required
                        </p>
                        <p className="text-sm text-muted-foreground">
                          All manuscript entries require Admin approval before
                          becoming active. Restricted manuscripts can only be
                          read inside the library.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Panel */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    className="w-full gap-2"
                    variant="hero"
                    onClick={handleSubmitForApproval}
                    disabled={isSubmitting}
                  >
                    <Save className="h-4 w-4" />
                    {isSubmitting ? "Submitting..." : "Submit for Approval"}
                  </Button>
                  <Button
                    className="w-full gap-2"
                    variant="outline"
                    onClick={handleSaveAsDraft}
                    disabled={isSubmitting}
                  >
                    <FileText className="h-4 w-4" />
                    Save as Draft
                  </Button>
                  <Button
                    className="w-full gap-2"
                    variant="ghost"
                    onClick={handleResetForm}
                    disabled={isSubmitting}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset Form
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="inquiry" className="space-y-6">
            {/* Filter Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Search Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="space-y-2">
                    <Label>Granth Name</Label>
                    <Input
                      placeholder="Search granth..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mulkarta</Label>
                    <Input placeholder="Author name..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Tikakarta</Label>
                    <Input placeholder="Commentator..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Input placeholder="Century..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="All categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat.toLowerCase()}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="All languages" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {languages.map(lang => (
                          <SelectItem key={lang} value={lang.toLowerCase()}>
                            {lang}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Folio No</Label>
                    <Input type="number" placeholder="Folio..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input placeholder="Safe/Rack..." />
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button className="gap-2">
                    <Search className="h-4 w-4" />
                    Search
                  </Button>
                  <Button variant="outline" className="gap-2">
                    <RotateCcw className="h-4 w-4" />
                    Reset
                  </Button>
                  <div className="flex-1" />
                  <Button variant="outline" className="gap-2">
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                  <Button variant="outline" className="gap-2">
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results Table */}
            <Card>
              <CardContent className="pt-6">
                <div className="overflow-hidden rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/50">
                        <TableHead>Hastrap No</TableHead>
                        <TableHead>Granth Name</TableHead>
                        <TableHead>Mulkarta</TableHead>
                        <TableHead>Language</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Pages</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sampleManuscripts.filter(ms => {
                        const query = searchQuery.trim().toLowerCase();
                        return (
                          !query ||
                          ms.granthName.toLowerCase().includes(query) ||
                          ms.hastrapNo.toLowerCase().includes(query) ||
                          ms.mulkarta.toLowerCase().includes(query)
                        );
                      }).length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={20}
                            className="py-8 text-center text-muted-foreground"
                          >
                            No records available.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sampleManuscripts
                          .filter(ms => {
                            const query = searchQuery.trim().toLowerCase();
                            return (
                              !query ||
                              ms.granthName.toLowerCase().includes(query) ||
                              ms.hastrapNo.toLowerCase().includes(query) ||
                              ms.mulkarta.toLowerCase().includes(query)
                            );
                          })
                          .map(ms => (
                            <TableRow
                              key={ms.id}
                              className="hover:bg-secondary/30"
                            >
                              <TableCell className="font-medium">
                                {ms.hastrapNo}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {ms.granthName}
                                  {ms.restricted && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Lock className="h-3 w-3 text-amber-500" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          Read inside library only
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{ms.mulkarta}</TableCell>
                              <TableCell>{ms.language}</TableCell>
                              <TableCell>
                                <Badge variant="secondary">{ms.category}</Badge>
                              </TableCell>
                              <TableCell>
                                {ms.availablePages}/{ms.totalPages}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    ms.status === "Active"
                                      ? "default"
                                      : ms.status === "Pending"
                                        ? "secondary"
                                        : "destructive"
                                  }
                                >
                                  {ms.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button variant="ghost" size="icon">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon">
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </LMSLayout>
  );
}
